<template>
  <div class="numbering-list-item" :style="{ gap: `${markerTextSpacing !== undefined ? markerTextSpacing : 20}px` }">
    <!-- {{ markerStyle({ background: resolvedBg }) }} -->
    <!-- Явные шаблоны по типам для простой правки макета -->
    <NumberingMarkerSymbol v-if="type === NumberingMarkerEnum.One"
                     extra-class="rounded-[50%]"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Two"
                     extra-class="rounded-[50%]"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Three"
                     extra-class="rounded-[50%]"
                     :style-obj="filledStyle"
                     :text="showText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Four"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Five"
                     extra-class="rounded-[7px]"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Six"
                     extra-class="rounded-[50%]"
                     :style-obj="outlineStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Seven"
                     extra-class="text-[28px]"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Eight"
                     extra-class="text-[28px] font-[900] font-[system-ui]"
                     :style-obj="filledStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.Nine"
                     extra-class="rounded-[8px]"
                     :style-obj="filledStyle"
                     :text="showText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <!-- Нові маркери з SVG-підложкою (fon21, fon22): число поверх зображення -->
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.TwentyOne"
                     :style-obj="imageStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <NumberingMarkerSymbol v-else-if="type === NumberingMarkerEnum.TwentyTwo"
                     :style-obj="imageStyle"
                     :text="showZeroText"
                     :marker-vertical-offset="markerVerticalOffset" />
    <!-- fallback -->
    <NumberingMarkerSymbol v-else
                     :style-obj="filledStyle"
                     :text="true ? showZeroText : showText"
                     :marker-vertical-offset="markerVerticalOffset" />

    <div class="list-item-text" v-if="!labelDisabled">
      <div
        v-if="showTitle && title"
        class="list-item-title pb-1"
        v-html="title"
      ></div>
      <VBibleInteractive :html="text ?? ''" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue';
import { CssStyleHelpers, StyleUtils } from '../../utils/cssStyleHelpers';
import { NumberingMarker as NumberingMarkerEnum } from '../../composables/markers';
import { useMarkerTypes } from '../../composables/markers';
import NumberingMarkerSymbol from './symbols/NumberingMarkerSymbol.vue';

const props = defineProps<{
  text: string;
  title?: string;
  showTitle?: boolean;
  index: number;
  markerType: string;
  markerColor?: string;
  markerBackgroudColor?: string;
  markerTextColor?: string;
  labelDisabled?: boolean;
  isUseCustomMarkerColor?: boolean;
  markerTextSpacing?: number;
  markerVerticalOffset?: number;
  startIndex?: number;
}>();

const type = computed(() => props.markerType);
const showZeroText = computed(() => String(props.index + (props.startIndex ?? 1)).padStart(2, '0'));
const showText = computed(() => String(props.index + (props.startIndex ?? 1)));

// Доступ к конфигурации маркера через композабл
const { getMarkerConfig } = useMarkerTypes();

// Унифицированные аргументы для markerStyle: учитываем solid/gradient из конфигурации
const styleArgs = computed(() => {
  // Використовуємо кастомний колір тільки якщо isUseCustomMarkerColor = true
  if (props.isUseCustomMarkerColor) {
    const custom = (props.markerBackgroudColor || props.markerColor || '').trim();
    if (custom) {
      if (custom.startsWith('linear-gradient')) return {gradient: custom, textColor: resolvedTextColor.value} as any;
      return {background: custom, textColor: resolvedTextColor.value} as any;
    }
  }
  const cfg = getMarkerConfig(props.markerType);
  if (cfg?.backgroundGradient) return {gradient: cfg.backgroundGradient, textColor: cfg.textColor || '#ffffff'} as any;
  if (cfg?.backgroundColor) return {background: cfg.backgroundColor, textColor: cfg.textColor || '#ffffff'} as any;

  return {background: 'transparent', textColor: cfg?.textColor || '#ffffff'} as any;
});

const resolvedOutlineColor = computed(() => {
  // Використовуємо кастомний колір тільки якщо isUseCustomMarkerColor = true
  if (props.isUseCustomMarkerColor) {
    const custom = (props.markerBackgroudColor || props.markerColor || '').trim();
    if (custom) {
      return custom;
    }
  }
  // Для контура используем сплошной цвет из конфигурации, либо дефолт
  const cfg = getMarkerConfig(props.markerType);
  return cfg?.backgroundColor || '#14B8A6';
});

const resolvedTextColor = computed(() => {
  // Використовуємо кастомний колір тексту тільки якщо isUseCustomMarkerColor = true
  if (props.isUseCustomMarkerColor) {
    const custom = (props.markerTextColor || '').trim();
    if (custom) return custom;
  }
  const cfg = getMarkerConfig(props.markerType);
  return cfg?.textColor || '#ffffff';
});

const base = {
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  flexShrink: 0,
  minWidth: '40px'
} as const;

type MarkerStyleArgs = {background?: string; gradient?: string; textColor?: string; outlineColor?: string};

const filledStyle = computed(() => {
  const bg = styleArgs.value.gradient || styleArgs.value.background || '#8b5cf6';
  return {
    ...base,
    background: bg,
    color: styleArgs.value.textColor || resolvedTextColor.value
  } as any;
});

const imageStyle = computed(() =>
  CssStyleHelpers.buildSvgImageStyle(
    getMarkerConfig(props.markerType)?.path || '',
    resolvedTextColor.value,
    props.isUseCustomMarkerColor,
    props.markerBackgroudColor,
    props.markerColor,
  ) as any,
);

const outlineStyle = computed(() => {
  const color = resolvedOutlineColor.value || '#14B8A6';
  return {
    ...base,
    background: 'white',
    border: `2px solid ${color}`,
    color: resolvedTextColor.value,
    boxShadow: StyleUtils.boxShadow(resolvedTextColor.value, { alpha: 0.3, blur: 7, offsetX: 0, offsetY: 0 })
  } as any;
});

// colorToRgba доступен из CssStyleHelpers при необходимости
</script>

<style scoped>
.numbering-list-item {
  display: flex;
  align-items: baseline;
  width: 100%;
}

.numbering-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.list-item-text {
  flex: 1;
  font-size: 16px;
  line-height: 1.5;
  color: #374151;
}

.list-item-text :deep(b),
.list-item-text :deep(strong) {
  font-weight: 600;
}

.list-item-title {
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 4px;
}

/* Адаптивность */
@media (max-width: 768px) {
  .numbering-marker {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }
  
  .marker-text {
    font-size: 12px;
  }
  
  .list-item-text {
    font-size: 14px;
  }
}
</style>
